<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-car-dumper-opc">
    <nx-report-header title="翻车作业进度与效率">
      <div class="kb-header-actions">
        <span class="kb-toolbar-hint">业务日</span>
        <el-date-picker
          v-model="queryDate"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="业务日"
          size="small"
          style="width: 150px"
          @change="loadData"
        />
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <div class="kb-kpi-row cols-4">
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">班次完成率</div>
        <div class="kb-kpi-value">{{ shiftRateText }}</div>
        <div class="kb-kpi-sub">
          {{ tippedCarriages }} / {{ plannedCarriages }} 车厢
        </div>
        <div class="kb-progress">
          <i :style="{ width: progressWidth }" />
        </div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">实时效率</div>
        <div class="kb-kpi-value is-blue">{{ minutePerCarText }}</div>
        <div class="kb-kpi-sub">分钟/车 · {{ carsPerHourText }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">剩余作业量</div>
        <div class="kb-kpi-value is-warn">{{ remainingCarriages }}<span class="kb-unit">车厢</span></div>
        <div class="kb-kpi-sub">计划数 - 完成数</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">当前班次</div>
        <div class="kb-kpi-value is-md">{{ data.shiftName || '--' }}</div>
        <div class="kb-kpi-sub">系统自动识别</div>
      </div>
    </div>

    <div class="kb-main-row">
      <div class="kb-chart-card">
        <div class="kb-card-head">
          <span>效率趋势（每小时翻卸量）</span>
          <small>单位：车厢/小时</small>
        </div>
        <div ref="trendChart" class="kb-chart-box" />
      </div>

      <div class="kb-section kb-plan-section">
        <div class="kb-card-head">
          <span>当前作业</span>
          <small>{{ sceneLabel }}</small>
        </div>
        <div class="kb-plan-title">{{ currentTrainNumber }}</div>
        <div class="kb-plan-grid">
          <div>
            <label>当前计划</label>
            <strong>{{ currentPlanDone }} / {{ currentPlanTotal }}</strong>
            <span>车厢</span>
          </div>
          <div>
            <label>待翻车厢</label>
            <strong>{{ currentPlanPending }}</strong>
            <span>车厢</span>
          </div>
          <div>
            <label>作业中</label>
            <strong>{{ currentWorkingCarriages }}</strong>
            <span>车厢</span>
          </div>
        </div>
        <div class="kb-working-nos">
          <span>在翻车厢</span>
          <em>{{ workingCarriageNos }}</em>
        </div>
      </div>
    </div>

    <div class="kb-two-col">
      <div class="kb-section">
        <div class="kb-card-head">
          <span>待作业队列</span>
          <small>{{ queueRows.length }} 条</small>
        </div>
        <el-table :data="queueRows" border stripe size="small" max-height="360" style="width: 100%">
          <el-table-column prop="trainNumber" label="车次" min-width="120" show-overflow-tooltip />
          <el-table-column prop="planNo" label="计划号" min-width="120" show-overflow-tooltip />
          <el-table-column prop="railPlanPhaseText" label="阶段" width="130" show-overflow-tooltip />
          <el-table-column prop="plannedCarriages" label="计划" width="76" align="right" />
          <el-table-column prop="tippedCarriageCount" label="已翻" width="76" align="right" />
        </el-table>
      </div>

      <div class="kb-section">
        <div class="kb-card-head">
          <span>最近完成车厢</span>
          <small>{{ recentRows.length }} 条</small>
        </div>
        <el-table :data="recentRows" border stripe size="small" max-height="360" style="width: 100%">
          <el-table-column prop="trainNumber" label="车次" min-width="120" show-overflow-tooltip />
          <el-table-column prop="carriageNumber" label="车厢号" min-width="120" show-overflow-tooltip />
          <el-table-column prop="updateTime" label="完成时间" min-width="160">
            <template slot-scope="{ row }">{{ fmtTs(row.updateTime) }}</template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchCarDumperOpcMonitor } from './api'

export default {
  name: 'KanbanCarDumperOpcMonitor',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      queryDate: null,
      data: {
        shiftName: '',
        shiftPlannedCarriages: 0,
        shiftTippedCarriages: 0,
        shiftCompletionRatePercent: null,
        currentWorkingCarriages: null,
        realtimeEfficiencyCarsPerHour: null,
        hourLabels: [],
        hourlyTippedDelta: [],
        queue: [],
        recentCarriages: [],
        currentPlan: { scene: '', plan: {} }
      },
      trendChart: null
    }
  },
  computed: {
    plannedCarriages() {
      return Number(this.data.shiftPlannedCarriages) || 0
    },
    tippedCarriages() {
      return Number(this.data.shiftTippedCarriages) || 0
    },
    remainingCarriages() {
      return Math.max(0, this.plannedCarriages - this.tippedCarriages)
    },
    shiftRate() {
      const raw = this.data.shiftCompletionRatePercent
      const n = raw == null || raw === '' ? NaN : Number(raw)
      if (Number.isFinite(n)) return n
      if (this.plannedCarriages <= 0) return 0
      return (this.tippedCarriages / this.plannedCarriages) * 100
    },
    shiftRateText() {
      return `${this.shiftRate.toFixed(1)}%`
    },
    progressWidth() {
      return `${Math.min(100, Math.max(0, this.shiftRate))}%`
    },
    minutePerCarText() {
      const carsPerHour = Number(this.data.realtimeEfficiencyCarsPerHour)
      if (!Number.isFinite(carsPerHour) || carsPerHour <= 0) return '--'
      return `${(60 / carsPerHour).toFixed(1)}`
    },
    carsPerHourText() {
      const carsPerHour = Number(this.data.realtimeEfficiencyCarsPerHour)
      if (!Number.isFinite(carsPerHour) || carsPerHour <= 0) return '暂无效率样本'
      return `${carsPerHour.toFixed(2)} 车厢/小时`
    },
    currentPlan() {
      return (this.data.currentPlan && this.data.currentPlan.plan) || {}
    },
    currentTrainNumber() {
      return this.currentPlan.trainNumber || '暂无当前计划'
    },
    currentPlanTotal() {
      return Number(this.currentPlan.plannedCarriages) || 0
    },
    currentPlanDone() {
      return Number(this.currentPlan.tippedCarriageCount) || 0
    },
    currentPlanPending() {
      if (this.currentPlan.pendingCarriageCount == null) {
        return Math.max(0, this.currentPlanTotal - this.currentPlanDone)
      }
      return Number(this.currentPlan.pendingCarriageCount) || 0
    },
    currentWorkingCarriages() {
      const n = this.data.currentWorkingCarriages
      return n != null && n !== '' ? Number(n) || 0 : 0
    },
    workingCarriageNos() {
      const list = this.currentPlan.workingCarriageNumbers || []
      return list.length ? list.join('、') : '--'
    },
    sceneLabel() {
      const s = this.data.currentPlan && this.data.currentPlan.scene
      const map = {
        WORKING: '正在作业',
        PENDING: '待作业/待确认',
        LAST_COMPLETED: '最近完成',
        NONE: '无计划'
      }
      return map[s] || '--'
    },
    queueRows() {
      return this.data.queue || []
    },
    recentRows() {
      return this.data.recentCarriages || []
    }
  },
  mounted() {
    this.queryDate = this.formatDateStr(new Date())
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.disposeChart()
  },
  methods: {
    formatDateStr(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    fmtTs(v) {
      if (!v) return '--'
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return String(v)
      const p = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    },
    async loadData() {
      this.loading = true
      try {
        const params = {}
        if (this.queryDate) params.queryDate = this.queryDate
        const raw = await fetchCarDumperOpcMonitor(params)
        this.data = Object.assign({}, this.data, raw || {})
        this.$nextTick(() => this.renderTrendChart())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    disposeChart() {
      if (this.trendChart) {
        this.trendChart.dispose()
        this.trendChart = null
      }
    },
    onResize() {
      if (this.trendChart) this.trendChart.resize()
    },
    renderTrendChart() {
      if (!this.$refs.trendChart) return
      this.disposeChart()
      const labels = this.data.hourLabels || []
      const values = this.data.hourlyTippedDelta || []
      this.trendChart = echarts.init(this.$refs.trendChart)
      this.trendChart.setOption({
        color: ['#2563eb'],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 46, right: 20, top: 30, bottom: 40 },
        xAxis: {
          type: 'category',
          data: labels,
          axisLabel: { color: '#64748b', rotate: labels.length > 14 ? 30 : 0 }
        },
        yAxis: {
          type: 'value',
          name: '车厢/小时',
          splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } }
        },
        series: [{
          name: '翻卸量',
          type: 'bar',
          barMaxWidth: 28,
          data: values.map((v) => Number(v) || 0),
          itemStyle: { borderRadius: [4, 4, 0, 0] }
        }]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
